import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import { customEventKeys } from 'hooks/customEvents/useCustomEvents';
import { toUtcIso } from '../../../utils/dateTime';
import type { GeoLocation } from 'components/GeoLocationComponent/GeoLocationComponent';
import type { ResourceRequirement } from 'components/IAP/ResourceRequirements';
import type { HazardRisk } from 'components/IAP/HazardsRisks';
import type {
  AttachmentFile,
  AttachmentLink,
} from 'components/Actions/ActionAttachments';
import { IapScope, ORGANIZATION } from '../../IAP/ICSConstants';

export type OrgActionAttachments = {
  files?: AttachmentFile[];
  links?: AttachmentLink[];
};

// Fields a user can set on an org action, shared by the response and request types
type OrgActionFields = {
  name: string;
  description: string | null;
  checklist: { label: string; checked: boolean }[] | null;
  poc: string | null;
  briefedPoc: boolean;
  status: string | null;
  parentId: UUID | null;
  eventId: UUID | null;
  severity: string | null;
  notificationTemplateId: UUID | null;
  resourceRequirements: ResourceRequirement[] | null;
  hazardsRisks: HazardRisk[] | null;
  attachments: OrgActionAttachments | null;
  symbol: string | null;
  location: GeoLocation | null;
  aors: string[] | null;
  blockedBy: UUID[] | null;
};

export type OrgActionBase = OrgActionFields & {
  id: UUID;
  actionId: UUID;
  archived: boolean;
  startDate: string | null;
  targetDate: string | null;
  actualDate: string | null;
  utcOffset?: number;
};

export type GetOrgActionResponse = OrgActionBase & {
  groupGuid: UUID;
  userGuid: UUID;
  userName?: string;
  pocUserName?: string;
  version: number;
  timestamp: string;
  index?: number;
};

// Only name is required; dates accept a Date or an ISO string
export type CreateOrgActionReqData = Partial<OrgActionFields> & {
  name: string;
  startDate?: Date | string | null;
  targetDate?: Date | string | null;
  actualDate?: Date | string | null;
  itemId?: UUID | null;
};

// The backend keeps the latest value of any field left out
export type EditOrgActionRequest = Partial<CreateOrgActionReqData> & {
  actionId: UUID;
  archived?: boolean;
};

export const keys = {
  base: ['org_actions'] as const,
  byGroupAndUser: (groupGuid: string, userId: string) =>
    [...keys.base, groupGuid, userId] as const,
  // Extends byGroupAndUser so invalidating that key also refreshes linked lists
  byItem: (groupGuid: string, userId: string, itemId: string) =>
    [...keys.byGroupAndUser(groupGuid, userId), 'item', itemId] as const,
} as const;

const browserUtcOffset = () => -new Date().getTimezoneOffset();

// Event tasks are org actions, so event queries are refreshed too
const useInvalidateOrgActions = () => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: keys.byGroupAndUser(groupGuid, userGuid),
    });
    queryClient.invalidateQueries({ queryKey: customEventKeys.base });
  };
};

export const useOrgActions = (
  scope: IapScope = ORGANIZATION,
  filters: { itemId?: UUID } = {}
) => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);
  const { itemId } = filters;
  return useQuery({
    queryKey: itemId
      ? keys.byItem(groupGuid, userGuid, itemId)
      : keys.byGroupAndUser(groupGuid, userGuid),
    queryFn: async () => {
      const resp = await axios.get<GetOrgActionResponse[]>(
        `${config.apiGateway.ics_org_actions}/${groupGuid}`,
        { params: itemId ? { itemId } : undefined }
      );
      return resp.data.map((a) => ({
        ...a,
        startDate: toUtcIso(a.startDate),
        targetDate: toUtcIso(a.targetDate),
        actualDate: toUtcIso(a.actualDate),
        timestamp: toUtcIso(a.timestamp),
      }));
    },
    enabled: scope === ORGANIZATION && !!groupGuid,
  });
};

export const useCreateOrgAction = () => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);
  const invalidate = useInvalidateOrgActions();
  return useMutation({
    mutationFn: async (data: CreateOrgActionReqData) => {
      return await axios.post<GetOrgActionResponse>(
        `${config.apiGateway.ics_org_actions}/${groupGuid}`,
        { ...data, userGuid, utcOffset: browserUtcOffset() }
      );
    },
    onSuccess: invalidate,
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useUpdateOrgAction = () => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);
  const invalidate = useInvalidateOrgActions();
  return useMutation({
    mutationFn: async (data: EditOrgActionRequest) => {
      return await axios.put(
        `${config.apiGateway.ics_org_actions}/${groupGuid}/${data.actionId}`,
        { ...data, userGuid, utcOffset: browserUtcOffset() }
      );
    },
    onSuccess: invalidate,
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
